import React, { useState, useEffect } from 'react';
import {useNavigate} from 'react-router-dom';
import { getBoostOrders, updateBoostOrderStatus, expireBoost } from '../../services/api';
import { 
  FiSearch, 
  FiFilter, 
  FiRefreshCw, 
  FiEye,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiDollarSign,
  FiCalendar,
  FiTrendingUp,
  FiShoppingBag,
  FiUser,
  FiPackage,
  FiArrowLeft,
  FiArrowRight,
  FiZap,
  FiActivity,
  FiPercent,
  FiFileText
} from 'react-icons/fi';

function BoostOrders({ admin }) {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    date_from: '',
    date_to: '',
    page: 1,
    limit: 20
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [showFilters, setShowFilters] = useState(false);
  const [stats, setStats] = useState({
    total_orders: 0,
    total_revenue: 0,
    active_boosts: 0,
    conversion_rate: 0
  });

  useEffect(() => {
    fetchOrders();
  }, [filters, pagination.page]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      Object.keys(params).forEach(key => {
        if (params[key] === '') {
          delete params[key];
        }
      });

      const response = await getBoostOrders(params);
      if (response.success) {
        setOrders(response.data.orders || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0,
          pages: response.data.pagination?.pages || 0
        }));
        
        // Calculate stats
        calculateStats(response.data.orders || []);
      } else {
        setError(response.message || 'Failed to load orders');
      }
    } catch (err) {
      setError('Failed to load orders: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (orderList) => {
    const stats = {
      total_orders: orderList.length,
      total_revenue: 0,
      active_boosts: 0,
      paid_orders: 0,
      pending_orders: 0,
      failed_orders: 0,
      cancelled_orders: 0
    };
  
    orderList.forEach(order => {
      // Track all statuses
      switch (order.payment_status) {
        case 'paid':
          // ONLY add revenue for PAID orders
          stats.total_revenue += parseFloat(order.amount || 0);
          stats.paid_orders++;
          // Add active boosts from paid orders
          stats.active_boosts += parseInt(order.active_boosts || 0);
          break;
        case 'pending':
          stats.pending_orders++;
          break;
        case 'failed':
          stats.failed_orders++;
          break;
        case 'cancelled':
          stats.cancelled_orders++;
          break;
        default:
          // Handle any other statuses
          break;
      }
    });
  
    // Calculate conversion rate (paid / total)
    stats.conversion_rate = stats.total_orders > 0 ? (stats.paid_orders / stats.total_orders) * 100 : 0;
    
    // Calculate average order value (for paid orders only)
    stats.avg_order_value = stats.paid_orders > 0 ? (stats.total_revenue / stats.paid_orders) : 0;
    
    // Calculate pending revenue potential
    stats.pending_revenue = orderList
      .filter(order => order.payment_status === 'pending')
      .reduce((sum, order) => sum + parseFloat(order.amount || 0), 0);
    
    // Calculate cancelled revenue (lost revenue)
    stats.cancelled_revenue = orderList
      .filter(order => order.payment_status === 'cancelled')
      .reduce((sum, order) => sum + parseFloat(order.amount || 0), 0);
    
    setStats(stats);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    if (!window.confirm(`Change order status to "${newStatus}"?`)) {
      return;
    }

    try {
      const response = await updateBoostOrderStatus(orderId, newStatus);
      if (response.success) {
        setSuccessMessage(`Order status updated to ${newStatus} successfully!`);
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchOrders();
      } else {
        throw new Error(response.message || 'Update failed');
      }
    } catch (err) {
      setError('Failed to update order status: ' + err.message);
    }
  };

  const handleExpireBoost = async (orderId) => {
    if (!window.confirm('Are you sure you want to expire this boost? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await expireBoost(orderId);
      if (response.success) {
        setSuccessMessage('Boost expired successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchOrders();
      } else {
        throw new Error(response.message || 'Expire failed');
      }
    } catch (err) {
      setError('Failed to expire boost: ' + err.message);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-3 py-1 text-xs font-medium rounded-full flex items-center justify-center w-28";
    
    const statusConfig = {
      'paid': { 
        bg: 'bg-green-900/30', 
        text: 'text-green-400 border-green-800/50', 
        label: 'Paid', 
        icon: <FiCheckCircle className="mr-1" /> 
      },
      'pending': { 
        bg: 'bg-yellow-900/30', 
        text: 'text-yellow-400 border-yellow-800/50', 
        label: 'Pending', 
        icon: <FiClock className="mr-1" /> 
      },
      'failed': { 
        bg: 'bg-red-900/30', 
        text: 'text-red-400 border-red-800/50', 
        label: 'Failed', 
        icon: <FiXCircle className="mr-1" /> 
      },
      'cancelled': { 
        bg: 'bg-gray-700', 
        text: 'text-gray-400 border-gray-600', 
        label: 'Cancelled', 
        icon: <FiXCircle className="mr-1" /> 
      }
    };

    const config = statusConfig[status] || { 
      bg: 'bg-gray-700', 
      text: 'text-gray-400', 
      label: status, 
      icon: <FiAlertCircle className="mr-1" /> 
    };
    
    return (
      <span className={`${baseClasses} ${config.bg} ${config.text} border ${config.border || 'border-transparent'}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  const getBoostBadge = (activeBoosts) => {
    if (activeBoosts > 0) {
      return (
        <span className="px-2 py-1 bg-blue-900/30 text-blue-400 text-xs font-medium rounded-full border border-blue-800/50 flex items-center">
          <FiZap className="mr-1" size={12} />
          {activeBoosts} Active
        </span>
      );
    }
    return null;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      search: '',
      date_from: '',
      date_to: '',
      page: 1,
      limit: 20
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Loading Skeleton
  if (loading && orders.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="animate-pulse">
          {/* Header Skeleton */}
          <div className="mb-6">
            <div className="h-8 bg-gray-700 rounded w-1/4 mb-2"></div>
            <div className="h-4 bg-gray-700 rounded w-1/2"></div>
          </div>

          {/* Stats Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-800 rounded-xl border border-gray-700 p-5">
                <div className="h-4 bg-gray-700 rounded w-1/2 mb-4"></div>
                <div className="h-8 bg-gray-700 rounded w-1/4 mb-2"></div>
                <div className="h-4 bg-gray-700 rounded w-3/4"></div>
              </div>
            ))}
          </div>

          {/* Table Skeleton */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="p-6 border-b border-gray-700">
                <div className="flex justify-between items-center">
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-700 rounded w-48"></div>
                    <div className="h-3 bg-gray-700 rounded w-32"></div>
                  </div>
                  <div className="h-6 bg-gray-700 rounded w-20"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-900 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              <span className="text-[#ff660e]">Boost</span> Orders
            </h1>
            <p className="text-gray-400 mt-1">Manage and track listing boost orders and revenue</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchOrders}
              className="px-4 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-all duration-200 font-medium flex items-center hover:scale-[1.02] shadow-lg"
            >
              <FiRefreshCw className="mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-900/20 border border-green-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiCheckCircle className="text-green-400 text-xl" />
            <p className="text-green-300 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-400 hover:text-green-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiAlertCircle className="text-red-400 text-xl" />
            <p className="text-red-300">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-400 hover:text-red-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Stats Summary */}
<div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
  {/* Total Orders */}
  <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-400 text-sm font-medium">Total Orders</p>
        <p className="text-2xl font-bold text-white mt-1 group-hover:text-[#ff660e] transition-colors">
          {stats.total_orders}
        </p>
        <div className="flex flex-wrap gap-1 mt-1">
          <span className="text-xs px-1.5 py-0.5 bg-green-900/30 text-green-400 rounded">
            {stats.paid_orders} paid
          </span>
          <span className="text-xs px-1.5 py-0.5 bg-yellow-900/30 text-yellow-400 rounded">
            {stats.pending_orders} pending
          </span>
          <span className="text-xs px-1.5 py-0.5 bg-gray-700 text-gray-400 rounded">
            {stats.cancelled_orders} cancelled
          </span>
        </div>
      </div>
      <div className="w-10 h-10 rounded-lg bg-blue-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
        <FiFileText className="text-blue-400" />
      </div>
    </div>
  </div>
  
  {/* Confirmed Revenue (Paid Orders Only) */}
  <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-400 text-sm font-medium">Confirmed Revenue</p>
        <p className="text-2xl font-bold text-green-400 mt-1 group-hover:text-green-300 transition-colors">
          {formatCurrency(stats.total_revenue)}
        </p>
        <p className="text-sm text-gray-500 mt-1">
          {formatCurrency(stats.avg_order_value)} avg
        </p>
      </div>
      <div className="w-10 h-10 rounded-lg bg-green-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
        <FiDollarSign className="text-green-400" />
      </div>
    </div>
  </div>
  
  {/* Active Boosts */}
  <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-400 text-sm font-medium">Active Boosts</p>
        <p className="text-2xl font-bold text-[#ff660e] mt-1 group-hover:text-orange-400 transition-colors">
          {stats.active_boosts}
        </p>
        <p className="text-sm text-gray-500 mt-1">
          From {stats.paid_orders} paid orders
        </p>
      </div>
      <div className="w-10 h-10 rounded-lg bg-[#ff660e]/20 flex items-center justify-center group-hover:scale-110 transition-transform">
        <FiZap className="text-[#ff660e]" />
      </div>
    </div>
  </div>
  
  {/* Pending Revenue */}
  <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-400 text-sm font-medium">Pending Revenue</p>
        <p className="text-2xl font-bold text-yellow-400 mt-1 group-hover:text-yellow-300 transition-colors">
          {formatCurrency(stats.pending_revenue)}
        </p>
        <p className="text-sm text-gray-500 mt-1">
          {stats.pending_orders} order{stats.pending_orders !== 1 ? 's' : ''}
        </p>
      </div>
      <div className="w-10 h-10 rounded-lg bg-yellow-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
        <FiClock className="text-yellow-400" />
      </div>
    </div>
  </div>
  
  {/* Conversion Rate */}
  <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-400 text-sm font-medium">Conversion Rate</p>
        <p className="text-2xl font-bold text-blue-400 mt-1 group-hover:text-blue-300 transition-colors">
          {stats.conversion_rate.toFixed(1)}%
        </p>
        <p className="text-sm text-gray-500 mt-1">
          {stats.paid_orders}/{stats.total_orders} orders
        </p>
      </div>
      <div className="w-10 h-10 rounded-lg bg-blue-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
        <FiPercent className="text-blue-400" />
      </div>
    </div>
  </div>
  
  {/* Lost Revenue (Cancelled) */}
  <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-400 text-sm font-medium">Cancelled Orders</p>
        <p className="text-2xl font-bold text-gray-400 mt-1 group-hover:text-gray-300 transition-colors">
          {stats.cancelled_orders}
        </p>
        <p className="text-sm text-gray-500 mt-1">
          {formatCurrency(stats.cancelled_revenue)} lost
        </p>
      </div>
      <div className="w-10 h-10 rounded-lg bg-gray-700 flex items-center justify-center group-hover:scale-110 transition-transform">
        <FiXCircle className="text-gray-400" />
      </div>
    </div>
  </div>
</div>

      {/* Filters Card */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 mb-6">
        <div className="p-4 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FiFilter className="text-[#ff660e]" />
              <h3 className="font-medium text-white">Filters</h3>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="text-sm text-[#ff660e] hover:text-orange-400 font-medium flex items-center transition-colors"
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="p-4 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">Search</label>
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search by user, email, listing, or order ID..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="w-full pl-10 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white placeholder-gray-500"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white"
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="failed">Failed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {/* Date From */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">From Date</label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => handleFilterChange('date_from', e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white"
                />
              </div>

              {/* Date To */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">To Date</label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => handleFilterChange('date_to', e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white"
                />
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex justify-between items-center mt-6 pt-6 border-t border-gray-700">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-gray-400 hover:text-gray-300 text-sm font-medium hover:bg-gray-700 rounded-lg transition-colors"
              >
                Clear All Filters
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowFilters(false)}
                  className="px-4 py-2 text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors font-medium hover:scale-[1.02]"
                >
                  Cancel
                </button>
                <button
                  onClick={fetchOrders}
                  className="px-4 py-2 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium flex items-center hover:scale-[1.02]"
                >
                  <FiFilter className="mr-2" />
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Orders Table */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Order & User Details
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Package & Listing
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Amount & Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Timeline
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-700">
              {orders.map((order) => (
                <tr key={order.order_id} className="hover:bg-gray-750 transition-colors duration-150 group">
                  <td className="px-6 py-4">
                    <div>
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="px-2 py-1 bg-gray-700 text-gray-300 text-xs font-medium rounded">
                          #{order.order_id}
                        </div>
                        {getBoostBadge(order.active_boosts)}
                      </div>
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold">
                          {order.first_name?.charAt(0)}{order.last_name?.charAt(0)}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-white group-hover:text-[#ff660e] transition-colors">
                            {order.first_name} {order.last_name}
                          </div>
                          <div className="text-xs text-gray-400">
                            {order.user_email}
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">
                        User ID: {order.user_id}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <div className="px-2 py-1 bg-blue-900/30 text-blue-400 text-sm font-medium rounded border border-blue-800/50 flex items-center">
                          <FiPackage className="mr-1" />
                          {order.package_name}
                        </div>
                        <div className="px-2 py-1 bg-purple-900/30 text-purple-400 text-sm font-medium rounded border border-purple-800/50 flex items-center">
                          <FiCalendar className="mr-1" />
                          {order.duration_days} days
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-white truncate max-w-xs">
                          {order.listing_title}
                        </div>
                        {order.seller_store && (
                          <div className="text-xs text-gray-400">
                            Store: {order.seller_store}
                          </div>
                        )}
                        <div className="text-xs text-gray-500">
                          Listing ID: {order.listing_id}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-2">
                      <div className="text-2xl font-bold text-[#ff660e] group-hover:text-orange-400 transition-colors">
                        {formatCurrency(order.amount)}
                      </div>
                      <div className="space-y-1">
                        {getStatusBadge(order.payment_status)}
                        <div className="text-xs text-gray-500">
                          Payment: {order.payment_method || 'N/A'}
                        </div>
                        {order.transaction_id && (
                          <div className="text-xs text-gray-500 truncate max-w-xs">
                            TXN: {order.transaction_id}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm space-y-2">
                      <div>
                        <div className="font-medium text-white">
                          Created: {formatDate(order.created_at)}
                        </div>
                        <div className="text-gray-500 text-xs">
                          {formatTime(order.created_at)}
                        </div>
                      </div>
                      {order.boost_start && (
                        <div>
                          <div className="font-medium text-green-400">
                            Started: {formatDate(order.boost_start)}
                          </div>
                          <div className="text-gray-500 text-xs">
                            {formatTime(order.boost_start)}
                          </div>
                        </div>
                      )}
                      {order.boost_end && (
                        <div>
                          <div className="font-medium text-red-400">
                            Ends: {formatDate(order.boost_end)}
                          </div>
                          <div className="text-gray-500 text-xs">
                            {formatTime(order.boost_end)}
                          </div>
                        </div>
                      )}
                      {order.updated_at && order.updated_at !== order.created_at && (
                        <div>
                          <div className="font-medium text-gray-400">
                            Updated: {formatDate(order.updated_at)}
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col space-y-2 min-w-[180px]">
                      {/* Status Update Dropdown */}
                      {admin.role === 'admin' && (
                        <select
                          className="w-full px-3 py-1.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent text-white text-sm transition-all duration-200 hover:scale-[1.02]"
                          value={order.payment_status}
                          onChange={(e) => handleStatusUpdate(order.order_id, e.target.value)}
                        >
                          <option value="pending">Pending</option>
                          <option value="paid">Paid</option>
                          <option value="failed">Failed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      )}
                      
                      {/* Expire Boost Button */}
                      {order.payment_status === 'paid' && order.active_boosts > 0 && (
                        <button
                          onClick={() => handleExpireBoost(order.order_id)}
                          className="px-3 py-1.5 bg-red-900/30 hover:bg-red-900/50 text-red-400 hover:text-red-300 rounded-lg text-sm font-medium flex items-center justify-center transition-all duration-200 hover:scale-[1.02] border border-red-800/50"
                        >
                          <FiXCircle className="mr-2" />
                          Expire Boost
                        </button>
                      )}
                      
                      {/* View Details Button */}
                      <button
                        onClick={() => navigate(`/admin/boost/orders/${order.order_id}`)}
                        className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded-lg text-sm font-medium flex items-center justify-center transition-all duration-200 hover:scale-[1.02]"
                      >
                        <FiEye className="mr-2" />
                        View Details
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {orders.length === 0 && !loading && (
          <div className="text-center py-16">
            <div className="text-gray-600 text-8xl mb-6">🚀</div>
            <h3 className="text-xl font-medium text-white mb-2">No boost orders found</h3>
            <p className="text-gray-400 mb-6">Try adjusting your search or filters</p>
            <button
              onClick={clearFilters}
              className="px-6 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium hover:scale-[1.02]"
            >
              Clear Filters
            </button>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-gray-900 px-6 py-4 border-t border-gray-700">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-400">
                Showing <span className="font-semibold text-white">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                <span className="font-semibold text-white">
                  {Math.min(pagination.page * pagination.limit, pagination.total)}
                </span>{' '}
                of <span className="font-semibold text-white">{pagination.total}</span> orders
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 border border-gray-700 rounded-lg text-sm font-medium text-gray-400 bg-gray-800 hover:bg-gray-700 hover:text-white hover:border-[#ff660e] disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-all duration-200 hover:scale-[1.02]"
                >
                  <FiArrowLeft className="mr-2" />
                  Previous
                </button>
                
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    let pageNum;
                    if (pagination.pages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.pages - 2) {
                      pageNum = pagination.pages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-10 h-10 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105 ${
                          pagination.page === pageNum
                            ? 'bg-[#ff660e] text-white shadow-lg'
                            : 'border border-gray-700 text-gray-400 hover:bg-gray-700 hover:text-white'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="px-4 py-2 border border-gray-700 rounded-lg text-sm font-medium text-gray-400 bg-gray-800 hover:bg-gray-700 hover:text-white hover:border-[#ff660e] disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-all duration-200 hover:scale-[1.02]"
                >
                  Next
                  <FiArrowRight className="ml-2" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default BoostOrders;